import { CORNER_FRACTION, useCornerLongPress } from './useCornerLongPress';

/**
 * The invisible top-right corner that opens Betreuer-Modus.
 *
 * Both gestures live here — a two-second still hold, or five taps within four seconds. See
 * `useCornerLongPress` for why there are two and why they belong to an element rather than to
 * `document`.
 *
 * **Nothing is drawn.** The person must have no reason to wonder what this is, and a caregiver who
 * knows the gesture does not need a target to aim at. `aria-hidden` because there is nothing here to
 * announce, and it is not a route the person should be offered.
 *
 * The one thing it costs: with the content band wider than about 65 % the top-right of the output bar
 * stops opening Partner-Ansicht, because this sits above it. The bar spans the whole band and the rest
 * of it still works, which is a small price for a care mode that can always be reached — the state
 * this replaces was one slider drag away from unreachable.
 */
export function CareEntryCorner({ onEnter }: { onEnter: () => void }): React.JSX.Element {
  const handlers = useCornerLongPress(onEnter);

  return (
    <div
      data-testid="care-entry-corner"
      aria-hidden="true"
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        width: `${CORNER_FRACTION * 100}%`,
        height: `${CORNER_FRACTION * 100}%`,
        // Above every surface, including the two that take the whole screen.
        zIndex: 10,
        // `none`, not `manipulation`: this is the one place in the app where the browser claiming a
        // touch for a pan is the failure mode, and there is nothing here to pan or double-tap.
        touchAction: 'none',
      }}
      {...handlers}
    />
  );
}
